import { color, font, query, space } from '@design/templateFns';
import styled from 'styled-components';

// Shared Components (some require a component and some are styled-components styles)
import { CardHeader, CardImage, CardSpacer } from '../BaseCard/BaseCard';
import { CardContentWrapper, CardWrapper } from '../style';

import { Dates } from '@components/Dates';
import Link from '@components/Link';
import { getHostWithProtocol } from '@util/blog';
import { FC } from 'react';

export interface ArticleCardProps {
  //image: ResolvedImageData;
  img?: any;
  headline?: string;
  date?: string;
  bodyText?: any; //TODO: change to BasicText | string | ReactNode once that is setup properly
  alt_text?: string;
  slug?: string;
  urlPrepend?: string;
  imageAspectRatio?: number;
  tags?: {
    slug: string;
    name: string;
  }[];
  backgroundColor?: boolean;
  locale: string;
}

const ArticleCard: FC<ArticleCardProps> = ({
  img,
  tags,
  headline,
  date,
  alt_text,
  bodyText,
  slug,
  urlPrepend,
  imageAspectRatio = 0.75,
  backgroundColor = false,
  locale,
}) => {
  const hostWithProtocol = getHostWithProtocol();
  const url = urlPrepend ? `${urlPrepend}/blog/${slug}` : `${hostWithProtocol}/blog/${slug}`;
  // Based on if a shop has it's own blog homepage

  return (
    <CardWrapper>
      {img && (
        <CardImage image={img} alt_text={alt_text} imageAspectRatio={imageAspectRatio} url={url} />
      )}

      {headline && <CardHeader header={headline} url={url} />}

      <CardSpacer />

      <CardContentWrapper>
        <StyledDescription>{bodyText as string}</StyledDescription>
        {date && (
          <StyledDate>
            <Dates startDate={date} locale={locale} />
          </StyledDate>
        )}
        {tags && (
          <TagsWrap $backgroundColor={backgroundColor}>
            {tags.map((tag) => (
              <Link
                key={tag.slug}
                href={urlPrepend ? `${urlPrepend}/blog?tag=${tag.slug}` : `/blog?tag=${tag.slug}`}
                scroll={false}
              >
                {tag.name}
              </Link>
            ))}
          </TagsWrap>
        )}
      </CardContentWrapper>
    </CardWrapper>
  );
};

export default ArticleCard;

const StyledDescription = styled.div`
  ${font('body-md-light')};
  padding-bottom: 0;
  margin-bottom: ${space('xl')};
  color: ${color('gray5')};
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2; /* number of lines to show */
  line-clamp: 2;
  -webkit-box-orient: vertical;

  @media screen and (${query.below('desktop')}) {
    ${font('body-sm-light')};
    margin-bottom: ${space('lg')};
  }
`;

const StyledDate = styled.div`
  display: inherit;
  ${font('eyebrow-sm')};
  text-transform: uppercase;
  color: ${color('gray4')};
  padding: 0 0 ${space('x5')};
  margin: 0;
  span {
    font-weight: 700;
  }
`;

const TagsWrap = styled.div<{ $backgroundColor: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  flex-wrap: wrap;
  column-gap: ${space('md')};
  row-gap: ${space('xs')};

  a {
    display: inline-block;
    ${font('body-xs')};
    text-decoration: none;
    white-space: nowrap;
    padding: 2px 8px 3px;
    color: ${color('gray5')};
    background-color: ${({ $backgroundColor }) =>
      $backgroundColor ? color('bg') : color('gray1')};
    border-radius: 4px;
    transition: background 0.2s ease-in-out;

    &:hover {
      background: ${({ $backgroundColor }) => ($backgroundColor ? color('gray1') : color('gray2'))};
    }
  }
`;
